import { useEffect, useState } from 'react';
import { Badge } from 'antd';
import { TableInProgress } from 'features/tables';
import { dataForAbsencesTable, getErrorData, getZupStatusName, sortingDate } from 'shared/helpers/utils';
import {
  useApproveAbsenceMutation,
  useApproveArrayAbsenceMutation,
  useLazyGetAllAbsenceQuery,
  useRejectAbsenceMutation,
  useRejectArrayAbsenceMutation,
} from 'app/services/edpApiV1/absence';
import { RequestsSearch, TColumns } from 'shared/types/types';
import { usePullSort } from 'shared/hooks';
import { SortCondition } from 'shared/enums';
import { PresetStatusColorType } from 'antd/lib/_util/colors';
import { useIndicators } from 'features/indicators';
import { ONE_MINUTE } from 'shared/constants';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from 'shared/hooks/use-notifications';
import { TPreparedAbsenceData } from 'shared/types/absence.types';
import { SearchKeysNews } from 'shared/enums/news';
import { getAllAvailableOrganizations } from 'entities/organizations/model/organizations';
import { useAppSelector } from 'app/store';
import style from './index.module.scss';

export const AbsenceInProgress = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [currentSize, setCurrentSize] = useState<number>(10);
  const [dataAbsence, setDataAbsence] = useState<TPreparedAbsenceData[] | []>([]);
  const [sortInfo, trigger] = usePullSort();
  const [search, setSearch] = useState<RequestsSearch>({ byId: undefined, fio: undefined });
  const allAvailableOrganizations = useAppSelector(getAllAvailableOrganizations);

  const onSearch = (data: string, key: SearchKeysNews) => {
    if (key === SearchKeysNews.SEARCH_ID) {
      setSearch({ fio: undefined, byId: data ? data : undefined });
    } else if (key === SearchKeysNews.SEARCH_NAME) {
      setSearch({ byId: undefined, fio: data ? data : undefined });
    } else {
      setSearch({ [key]: data ? data : undefined });
    }
  };
  // Добавляем пуллинг обновления списка  и обновляем индикаторы через хук useIndicators
  const [getAllAbsenceTrigger, resultAllAbsence] = useLazyGetAllAbsenceQuery({
    pollingInterval: ONE_MINUTE,
  });

  const [absenceApproveTrigger, absenceApproveResult] = useApproveAbsenceMutation({
    selectFromResult: result => ({ ...result, error: getErrorData(result.error) }),
    fixedCacheKey: 'approveAbsence',
  });
  const {
    isSuccess: isAbsenceApproveSuccess,
    isError: isAbsenceApproveError,
    error: errorAbsenceApprove,
    reset: resetAbsenceApprove,
  } = absenceApproveResult;

  const [absenceRejectTrigger, absenceRejectResult] = useRejectAbsenceMutation({
    selectFromResult: result => ({ ...result, error: getErrorData(result.error) }),
    fixedCacheKey: 'rejectAbsence',
  });
  const {
    isSuccess: isAbsenceRejectSuccess,
    isError: isAbsenceRejectError,
    error: errorAbsenceReject,
    reset: resetAbsenceReject,
  } = absenceRejectResult;

  const [absenceArrayApproveTrigger, absenceArrayApproveResult] = useApproveArrayAbsenceMutation({
    selectFromResult: result => ({ ...result, error: getErrorData(result.error) }),
    fixedCacheKey: 'approveArrayAbsence',
  });
  const {
    isSuccess: isApproveArrayAbsenceSuccess,
    isError: isApproveArrayAbsenceError,
    error: errorApproveArrayAbsence,
    reset: resetApproveArrayAbsence,
  } = absenceArrayApproveResult;
  const [absenceArrayRejectTrigger, absenceArrayRejectResult] = useRejectArrayAbsenceMutation({
    selectFromResult: result => ({ ...result, error: getErrorData(result.error) }),
    fixedCacheKey: 'rejectArrayAbsence',
  });

  const {
    isSuccess: isRejectArrayAbsenceSuccess,
    isError: isRejectArrayAbsenceError,
    error: errorRejectArrayAbsence,
    reset: resetRejectArrayAbsence,
  } = absenceArrayRejectResult;

  const onCell = (record: TColumns[number]) => ({
    className: style.pointer,
    onClick: () => {
      navigate(`/absence/user/${(record as TPreparedAbsenceData).id}`);
    },
  });

  const columns: TColumns = [
    {
      title: '№',
      dataIndex: 'id',
      key: 'key',
      width: 120,
      onCell,
    },
    {
      title: 'Дата',
      dataIndex: 'created',
      key: 'created',
      sortDirections: [SortCondition.ASCEND, SortCondition.DESCEND],
      sorter: (a, b) => sortingDate(a.created, b.created),
      onHeaderCell: () => ({
        onClick: () => trigger(),
      }),
      onCell,
    },
    {
      title: 'Период',
      dataIndex: 'period',
      key: 'period',
      onCell,
    },
    {
      title: 'ФИО',
      dataIndex: 'fio',
      key: 'fio',
      onCell,
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: (status: { type?: PresetStatusColorType; name: string }, record) => {
        return (
          <>
            <span>
              <Badge status={status.type} />
              {status.name}
            </span>
            {(() => {
              if ('zupRequestStatus' in record && record.zupRequestStatus) {
                return (
                  <div>
                    <Badge status={record.zupRequestStatus.type} />
                    {getZupStatusName(record.zupRequestStatus.type)}
                  </div>
                );
              }
            })()}
          </>
        );
      },
      onCell,
    },
  ];

  useEffect(() => {
    if (sortInfo.value === null)
      getAllAbsenceTrigger({
        type: 'myInWork',
        page: currentPage,
        size: currentSize,
        allAvailableOrganizations,
        ...search,
      });
    if (sortInfo.value === SortCondition.ASCEND)
      getAllAbsenceTrigger({
        type: 'myInWork',
        page: currentPage,
        size: currentSize,
        order: SortCondition.ASC,
        allAvailableOrganizations,
        ...search,
      });
    if (sortInfo.value === SortCondition.DESCEND)
      getAllAbsenceTrigger({
        type: 'myInWork',
        page: currentPage,
        size: currentSize,
        order: SortCondition.DESC,
        allAvailableOrganizations,
        ...search,
      });
  }, [getAllAbsenceTrigger, currentPage, sortInfo.value, currentSize, search, allAvailableOrganizations]);

  useEffect(() => {
    if (resultAllAbsence?.data?.data) {
      const dataTable = dataForAbsencesTable(resultAllAbsence.data.data);
      if (dataTable) {
        setDataAbsence(dataTable);
      }
    }
  }, [resultAllAbsence]);

  // подписываемся на обновление индикаторов, после обновления списка  и отключаем пуллинг запроса индикаторов
  useIndicators(resultAllAbsence.isSuccess, resultAllAbsence.isFetching, false);

  useNotifications(
    isAbsenceApproveSuccess,
    isAbsenceApproveError,
    'Отправлено на согласование',
    errorAbsenceApprove?.message,
    resetAbsenceApprove,
  );

  useNotifications(
    isApproveArrayAbsenceSuccess,
    isApproveArrayAbsenceError,
    'Отправлено на согласование',
    errorApproveArrayAbsence?.message,
    resetApproveArrayAbsence,
  );
  useNotifications(
    isAbsenceRejectSuccess,
    isAbsenceRejectError,
    'Заявление успешно закрыто',
    errorAbsenceReject?.message,
    resetAbsenceReject,
  );

  useNotifications(
    isRejectArrayAbsenceSuccess,
    isRejectArrayAbsenceError,
    'Заявления успешно закрыты',
    errorRejectArrayAbsence?.message,
    resetRejectArrayAbsence,
  );

  return (
    <>
      {dataAbsence && (
        <TableInProgress
          dataSource={dataAbsence}
          columns={columns}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          currentSize={currentSize}
          setCurrentSize={setCurrentSize}
          total={resultAllAbsence?.data?.pagination?.total}
          // для выбора нескольких записей
          rowSelectionType="checkbox"
          onSearch={onSearch}
        />
      )}
    </>
  );
};
